import React from 'react';

// The slack in pixels before the jump-to-top and jump-to-bottom buttons are shown.
const JUMP_SLACK_PX = 48;

// Distance from the bottom of the composer to the jump-to-bottom button, used to
// offset the composer padding.
const JUMP_COMPOSER_GAP_PX = -16;

// Distance from the top of the readable area to the jump-to-top button.
const JUMP_TOP_PX = 16;

// The dashboard's `h-16 lg:hidden` header plus the workspace top bar stick to
// the top of the page below `lg`, covering the conversation as it scrolls under
// them. Above `lg` the header is gone and the top bar sits outside the
// conversation's scroll container, so nothing overlays it.
const STICKY_CHROME_PX = 112;

// How many screens of readable conversation are worth a jump button.
const JUMP_MIN_SCREENS = 2.5;

/**
 * The conversation scrolls inside its own container at `lg` and up; narrower
 * viewports have no inner scroll container and scroll the page instead. Read
 * the live `overflow-y` to decide which element owns scrolling rather than
 * duplicating the breakpoint in JS, the way `scrollToBottom` in
 * `cara-input.tsx` does.
 *
 * `scrollingElement` is null in quirks mode, and in jsdom, so fall back to the
 * document element rather than the container we just ruled out.
 */
const resolveScroller = (container: HTMLElement): HTMLElement => {
  const { overflowY } = window.getComputedStyle(container);
  const scrolls = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  if (scrolls) {
    return container;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
};

/**
 * Decides whether the jump-to-top and jump-to-bottom buttons belong over the
 * conversation, and how far from each edge to place them.
 *
 * Attach the three refs to the conversation's scroll container, the message
 * list inside it, and the sticky composer. The offsets are distances from the
 * top and bottom of the scroller, ready for a `style` prop, because the
 * composer's height is only known at runtime and changes as the textarea wraps
 * and attachment chips are added.
 */
export const useJumpButtons = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const conversationRef = React.useRef<HTMLDivElement>(null);
  const composerRef = React.useRef<HTMLDivElement>(null);

  const [showJumpToTop, setShowJumpToTop] = React.useState(false);
  const [showJumpToBottom, setShowJumpToBottom] = React.useState(false);
  const [composerHeight, setComposerHeight] = React.useState(0);
  const [chromeHeight, setChromeHeight] = React.useState(0);

  const jumpTo = React.useCallback((edge: 'top' | 'bottom') => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const scroller = resolveScroller(container);
    scroller.scrollTo({
      top: edge === 'top' ? 0 : scroller.scrollHeight,
      behavior: 'smooth'
    });
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    const conversation = conversationRef.current;
    const composer = composerRef.current;
    if (!container || !conversation || !composer) {
      return;
    }

    let frame = 0;

    const measure = () => {
      const scroller = resolveScroller(container);
      const { scrollTop, scrollHeight, clientHeight } = scroller;

      // Below `lg` the page scrolls under the sticky app chrome, which hides
      // the top of the readable area the same way the composer hides the
      // bottom. The conversation's own scroll container has nothing over it.
      const chrome = scroller === container ? 0 : STICKY_CHROME_PX;
      setChromeHeight(chrome);

      // The composer sticks to the bottom of whatever is scrolling, so it
      // covers the tail of the conversation whenever the user is scrolled up.
      // Its whole box is opaque, so the button has to clear all of it and not
      // just the input inside. offsetHeight rather than a bounding rect because
      // AnimateIn animates the composer's transform as it enters.
      const overlay = composer.offsetHeight;
      setComposerHeight(overlay);

      // Weigh "long enough to warrant the buttons" against the conversation
      // alone: scrollHeight includes the composer, and the parts of the
      // viewport it and the chrome cover are not usable room to read in.
      const readableHeight = Math.max(clientHeight - overlay - chrome, 1);
      const isTallEnough = conversation.offsetHeight > readableHeight * JUMP_MIN_SCREENS;

      const isAtTop = scrollTop <= JUMP_SLACK_PX;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - JUMP_SLACK_PX;

      setShowJumpToTop(isTallEnough && !isAtTop);
      setShowJumpToBottom(isTallEnough && !isAtBottom);
    };

    // Measuring forces a layout, so collapse bursts of scroll and resize
    // notifications into a single measurement per frame.
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    measure();

    // Scroll events do not bubble, but they do propagate through the capture
    // phase, so a single listener covers the conversation container and the
    // document without having to know which of the two is scrolling.
    window.addEventListener('scroll', schedule, { passive: true, capture: true });

    // When the page is the scroller, a viewport height change moves the
    // thresholds without resizing any of the observed elements.
    window.addEventListener('resize', schedule);

    // A streaming response grows the conversation without firing a scroll or a
    // resize event, so the content itself has to be watched. The composer grows
    // too, as the textarea wraps and attachment chips are added.
    const observer = new ResizeObserver(schedule);
    observer.observe(conversation);
    observer.observe(container);
    observer.observe(composer);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, []);

  return {
    containerRef,
    conversationRef,
    composerRef,
    showJumpToTop,
    showJumpToBottom,
    topOffset: chromeHeight + JUMP_TOP_PX,
    bottomOffset: composerHeight + JUMP_COMPOSER_GAP_PX,
    jumpTo
  };
};
